import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';

export interface RecalculationProgress {
    jobId: string;
    totalItems: number;
    completed: number;
    failed: number;
    remaining: number;
    componentIndividualId: number;
    flowExecutionId: number;
    isSuccess: boolean;
}

export interface RecalculationCompleted {
    jobId: string;
    status: number;
    message: string;
    totalItems: number;
    completed: number;
    failed: number;
}

export type RecalculationEvent =
    | { type: 'started'; totalItems: number }
    | { type: 'progress'; data: RecalculationProgress }
    | { type: 'completed'; data: RecalculationCompleted };

@Injectable({ providedIn: 'root' })
export class RecalculationHubService {
    private hubConnection: signalR.HubConnection | null = null;
    private userId: string | null = null;

    private readonly _progress$ = new Subject<RecalculationProgress>();
    private readonly _completed$ = new Subject<RecalculationCompleted>();
    private readonly _reconnected$ = new Subject<void>();

    readonly progress$ = this._progress$.asObservable();
    readonly completed$ = this._completed$.asObservable();
    readonly reconnected$ = this._reconnected$.asObservable();

    get connectionId(): string | null {
        return this.hubConnection?.connectionId ?? null;
    }

    constructor(private msalService: MsalService, private ngZone: NgZone) { }

    async startConnection(): Promise<void> {
        if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected) {
            return;
        }

        if (this.hubConnection) {
            this.hubConnection = null;
        }

        await this.msalService.instance.initialize();

        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        const claims = account?.idTokenClaims as Record<string, any>;
        this.userId = claims?.['userid'] ?? account?.username;

        const hubUrl = `${environment.protectedResources.api.endpoint}/hubs/recalculation`;

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => this.getAccessToken(),
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
            })
            .withAutomaticReconnect()
            .build();

        this.hubConnection.onreconnecting((error) =>
            console.warn('[RecalculationHub] Reconnecting...', error)
        );

        this.hubConnection.onreconnected(async (connectionId) => {
            console.log('[RecalculationHub] Reconnected. New connectionId:', connectionId);
            if (this.userId) {
                await this.hubConnection!.invoke("JoinUserGroup", this.userId);
            }
            this.ngZone.run(() => this._reconnected$.next());
        });

        this.hubConnection.onclose((error) => {
            console.error('[RecalculationHub] Connection closed.', error);
            this.hubConnection = null;
        });

        this.hubConnection.on('RecalculationProgress', (data: RecalculationProgress) => {
            this.ngZone.run(() => this._progress$.next(data));
        });

        this.hubConnection.on('RecalculationCompleted', (data: RecalculationCompleted) => {
            this.ngZone.run(() => this._completed$.next(data));
        });

        await this.hubConnection.start();
        console.log('[RecalculationHub] Connected. connectionId:', this.hubConnection.connectionId);

        if (this.userId) {
            await this.hubConnection.invoke("JoinUserGroup", this.userId);
        }
    }

    async ensureConnection(): Promise<void> {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            return;
        }
        await this.startConnection();
    }

    private async getAccessToken(): Promise<string> {
        const account =
            this.msalService.instance.getActiveAccount() ??
            this.msalService.instance.getAllAccounts()[0];

        try {
            const result = await this.msalService.instance.acquireTokenSilent({
                scopes: environment.protectedResources.api.scopes,
                account,
            });
            console.log('[RecalculationHub] Access token acquired.');
            return result.accessToken;
        } catch (error) {
            console.error('[RecalculationHub] Failed to acquire token silently:', error);
            throw error;
        }
    }
}
